import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';

import { ImagePointer } from 'src/components/Svg/Images';
import { cn } from 'src/lib/utils';

import { Button } from './button';

const Control = ({ bgColor = '' }) => {
  return (
    <div className={cn('size-2  rounded-full shrink-0', bgColor)}>
      <span className="size-full block rounded-full border-[0.3px] border-black opacity-10" />
    </div>
  );
};

const Icon = ({ icon: IconComponent, className }: { icon: React.ElementType; className?: string }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentIcon, setCurrentIcon] = useState<React.ElementType>(IconComponent);

  useEffect(() => {
    if (IconComponent === currentIcon) {
      requestAnimationFrame(() => setIsVisible(true));
      return;
    }

    setIsVisible(false);

    const timeout = setTimeout(() => {
      setCurrentIcon(IconComponent);
      requestAnimationFrame(() => setIsVisible(true));
    }, 200); // Faster transition

    return () => clearTimeout(timeout);
  }, [IconComponent, currentIcon]);

  const CurrentIconComponent = currentIcon;

  return (
    <div
      className={cn(
        'absolute size-12.5 bg-white rounded flex items-center justify-center border border-muted',
        className,
      )}
    >
      <CurrentIconComponent
        className={cn(
          'size-7 text-foreground opacity-0 scale-75 transition-all duration-200 ease-out',
          isVisible && 'opacity-100 scale-100',
        )}
      />
    </div>
  );
};

type Props = {
  headerTitle: string;
  errorCode?: string;
  leftIcon?: React.ElementType;
  rightIcon?: React.ElementType;
};

const ErrorHero = ({ headerTitle, errorCode, leftIcon: LeftIcon, rightIcon: RightIcon }: Props) => {
  return (
    <section className="flex justify-center items-center p-6 rounded-xl min-h-[467px] bg-[linear-gradient(241deg,#75A0AF_2.33%,#367084_89.37%)] min-w-0">
      <div className="flex flex-col w-[439px] max-w-full min-h-[296px] mx-auto bg-secondary rounded-sm shadow-sm relative">
        <div className="flex items-center px-1.5 py-1 bg-white rounded-tl-sm rounded-tr-sm shadow-[0_-0.321px_0_0_rgba(0,0,0,0.05)_inset,0_0.321px_0_0_rgba(0,0,0,0.15)] shrink-0">
          <div className="flex gap-[5px] flex-1">
            <Control bgColor="bg-[#EC6A5E]" />
            <Control bgColor="bg-[#F5BF4F]" />
            <Control bgColor="bg-[#61C554]" />
          </div>
          <div className="flex-1 text-center text-muted-foreground text-[8px] font-bold">{headerTitle}</div>
          <span className="flex-1" />
        </div>
        <div className="p-6 grow flex flex-col justify-center">
          <div className="space-y-1.5 mb-4">
            {errorCode ? (
              <div className="text-[55px] font-bold text-foreground text-center">{errorCode}</div>
            ) : (
              <TriangleAlert className="size-9.5 text-foreground mb-8.5 mx-auto" />
            )}
            <div className="bg-muted-foreground/15 w-[200px] h-1.5 rounded-lg mx-auto" />
            <div className="bg-muted-foreground/15 w-[132px] h-1.5 rounded-lg mx-auto" />
          </div>
          <div className="text-center">
            <Button size="sm" className="relative text-[10px] h-6.5">
              Go back home
              <ImagePointer className="size-7! absolute top-2.5 right-0" />
            </Button>
          </div>
        </div>
        {LeftIcon && <Icon icon={LeftIcon} className="bottom-[46px] -left-[25px]" />}
        {RightIcon && <Icon icon={RightIcon} className="top-[33px] -right-[25px]" />}
      </div>
    </section>
  );
};

export { ErrorHero };
